import React, { useMemo } from 'react';
import MUIDataTable from 'mui-datatables';
import { Stack, Chip, Typography, Box, useMediaQuery } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import UserAvatar from '../../../common/UserAvatar';
import { useTheme } from '@emotion/react';
import CustomRow from './CustomRow';
import CustomEmptyTable from '../../../common/CustomEmptyTable';

const DelightsTable = ({ data, handleShowDetails }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  let noData = Boolean(!data || data?.length === 0);

  const columns = [
    {
      name: 'index',
      label: isMobile ? 'Requests' : 'No',
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const delight = tableMeta.rowData;
          return isMobile ? <CustomRow delight={delight} /> : <span>{tableMeta.rowIndex + 1}</span>;
        },
      },
    },
    {
      name: 'requestedBy',
      label: 'Requestor Name',
      options: {
        filter: false,
        sort: true,
        display: isMobile ? false : true,
        customBodyRender: (value, tableMeta) => {
          const row = tableMeta.rowData;
          const photo = row[3];
          return (
            <Stack direction={'row'} alignItems={'center'}>
              <UserAvatar src={photo} />
              <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                <Typography
                  noWrap
                  fontSize={'16px'}
                  sx={{
                    display: 'flex',
                    maxWidth: '300px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {value}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
    },
    {
      name: 'outingType',
      label: 'Outing Type',
      options: {
        filter: true,
        sort: false,
        display: isMobile ? false : true,
      },
    },
    {
      name: 'requesterPhoto',
      label: 'Requestor photo',
      options: {
        display: false,
        filter: true,
        sort: false,
      },
    },
    {
      name: 'numberOfMembers',
      label: 'Number Of Members',
      options: {
        filter: true,
        sort: false,
        display: isMobile ? false : true,
        customBodyRender: (value) => (
          <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
            {value}
            <div></div>
          </div>
        ),
      },
    },
    {
      name: 'dateApplied',
      label: 'Date Applied',
      options: {
        filter: true,
        sort: false,
        display: isMobile ? false : true,
      },
    },
    {
      name: 'hrStatus',
      label: 'HR Status',
      options: {
        filter: true,
        sort: false,
        display: isMobile ? false : true,
        customBodyRender: (value) => (
          <Stack direction={'row'}>
            <Chip
              style={{
                backgroundColor:
                  value === 'Pending'
                    ? 'rgba(255, 193, 7, 0.16)'
                    : value === 'Approved'
                    ? 'rgba(84, 214, 44, 0.16)'
                    : value === 'Rejected'
                    ? 'rgba(255, 72, 66, 0.16)'
                    : 'black',
                color:
                  value === 'Pending'
                    ? '#B78103'
                    : value === 'Approved'
                    ? '#229A16'
                    : value === 'Rejected'
                    ? 'red'
                    : 'black',
                borderRadius: '6px',
                width: '5.5rem',
                fontWeight: 'bold',
              }}
              label={value}
            />
          </Stack>
        ),
      },
    },
    {
      name: 'adminStatus',
      label: 'Admin Status',
      options: {
        filter: true,
        sort: false,
        display: isMobile ? false : true,
        customBodyRender: (value) => (
          <Stack direction={'row'}>
            <Chip
              style={{
                backgroundColor:
                  value === 'Pending'
                    ? 'rgba(255, 193, 7, 0.16)'
                    : value === 'Approved'
                    ? 'rgba(84, 214, 44, 0.16)'
                    : value === 'Rejected'
                    ? 'rgba(255, 72, 66, 0.16)'
                    : 'black',
                color:
                  value === 'Pending'
                    ? '#B78103'
                    : value === 'Approved'
                    ? '#229A16'
                    : value === 'Rejected'
                    ? 'red'
                    : 'black',
                borderRadius: '6px',
                width: '5.5rem',
                fontWeight: 'bold',
              }}
              label={value}
            />
          </Stack>
        ),
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        display: isMobile ? false : true,
        customBodyRender: (value, tableMeta) => (
          <Stack spacing={2} direction={'row'}>
            <Visibility
              onClick={() => handleShowDetails(data[tableMeta.rowIndex])}
              sx={{ cursor: 'default' }}
              aria-label='view'
            />
          </Stack>
        ),
      },
    },
  ];
  const handleRowClick = (row, { rowIndex }) => {
    handleShowDetails(data[rowIndex]);
  };
  const tableData = useMemo(() => {
    return data?.map((request, index) => ({
      index: index + 1,
      ...request,
      hrStatus: request.hrStatus,
      adminStatus: request.adminStatus,
      requestorName: request.requestedBy,
      requesterPhoto: request.requesterPhoto,
      dateApplied: new Date(request.dateRequested).toLocaleDateString(),
      outingType: request.requestType,
    }));
  }, [data]);

  const tableBodyMaxHeight = useMemo(() => {
    const rowsPerPage = 20;
    const rowHeight = 29;
    const headerHeight = 48;
    const paginationHeight = 56;

    return rowsPerPage * rowHeight + headerHeight + paginationHeight + 'px';
  }, []);
  return (
    <>
      <MUIDataTable
        title={null}
        data={tableData}
        columns={columns}
        options={{
          elevation: 0,
          selectableRows: false,
          rowsPerPage: 20,
          rowsPerPageOptions: [20, 30, 40],
          viewColumns: false,
          search: false,
          searchOpen: false,
          download: false,
          filter: false,
          print: false,
          tableBodyHeight: `calc(100vh - ${isMobile ? '245px' : '290px'})`,
          responsive: 'standard',
          onRowClick: handleRowClick,
        }}
        components={{
          ...(noData && {
            TableBody: CustomEmptyTable,
          }),
        }}
      />
    </>
  );
};

export default DelightsTable;
